export interface BrandConfig {
  name: string;
  domain: string;
  logo: {
    icon: string;
    text: string;
    url?: string;
  };
  favicon?: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    muted: string;
    background: string;
    foreground: string;
  };
  tagline: string;
  description: string;
  contact: {
    email: string;
    phone: string;
    whatsapp: string;
    address: string;
  };
  social: {
    twitter: string;
    linkedin: string;
    facebook: string;
    instagram?: string;
  };
}

export const brandConfigs: Record<string, BrandConfig> = {
  moyotools: {
    name: "MoyoTools",
    domain: "moyotools.com",
    logo: {
      icon: "",
      text: ""
    },
    favicon: "/images/favicon.png",
    colors: {
      primary: "hsl(221.2 83.2% 53.3%)", // Blue
      secondary: "hsl(210 40% 98%)",
      accent: "hsl(210 40% 98%)",
      muted: "hsl(210 40% 98%)",
      background: "hsl(0 0% 100%)",
      foreground: "hsl(222.2 84% 4.9%)"
    },
    tagline: "The Business Command Center for Visionary Entrepreneurs",
    description: "MoyoTools unifies your entire business infrastructure—from customer relationships to financial operations—creating a seamless, intelligent ecosystem that drives efficiency and accelerates growth.",
    contact: {
      email: "support@moyotools.com",
      phone: "+263771436742",
      whatsapp: "+263772724514",
      address: "68 Enterprise, Newlands, Harare, Zimbabwe"
    },
    social: {
      twitter: "https://twitter.com/moyotools",
      linkedin: "https://linkedin.com/company/moyotools",
      facebook: "https://facebook.com/moyotools",
      instagram: "https://instagram.com/moyotools"
    }
  },
  gerty: {
    name: "Gerty",
    domain: "gerty.co.zw",
    logo: {
      icon: "BoxIcon",
      text: "Gerty",
      url: "https://storage.googleapis.com/moyotools_images/gerty_logo.png"
    },
    favicon: "https://storage.googleapis.com/moyotools_images/gerty_favicon.png",
    colors: {
      primary: "hsl(186 34% 29%)", // #31656c turquoise
      secondary: "hsl(69 35% 69%)", // #bec980 green
      accent: "hsl(210 6% 72%)", // #b7b9bc cool grey
      muted: "hsl(270 5% 13%)", // #201e22 charcoal
      background: "hsl(0 0% 100%)", // white
      foreground: "hsl(270 5% 13%)" // #201e22 charcoal
    },
    tagline: "Intelligent Business Management for Africa",
    description: "Gerty empowers African businesses with comprehensive management tools designed for local market needs, integrating financial operations, inventory management, and customer relationships.",
    contact: {
      email: "support@gerty.co.zw",
      phone: "+263771436742",
      whatsapp: "+263772724514",
      address: "68 Enterprise, Newlands, Harare, Zimbabwe"
    },
    social: {
      twitter: "https://twitter.com/gertyapp",
      linkedin: "https://linkedin.com/company/gerty",
      facebook: "https://facebook.com/gertyapp"
    }
  },
  thatonrealty: {
    name: "Thaton Realty",
    domain: "thatonrealty.co.zw",
    logo: {
      icon: "HomeIcon",
      text: "Thaton Realty",
      url: "https://storage.googleapis.com/thatonrealty_images/thatonrealty.png"
    },
    favicon: "https://storage.googleapis.com/thatonrealty_images/thatonrealty_favicon.png",
    colors: {
      primary: "hsl(55 11% 58%)", // #959974
      secondary: "hsl(0 0% 100%)", // white
      accent: "hsl(0 0% 95%)", // light grey
      muted: "hsl(55 11% 85%)", // lighter version of primary
      background: "hsl(0 0% 100%)", // white
      foreground: "hsl(55 11% 20%)" // dark version of primary
    },
    tagline: "World class property management for your property",
    description: "Thaton Realty provides world-class property management services, offering comprehensive solutions for property owners and tenants with cutting-edge technology and personalized service.",
    contact: {
      email: "support@thatonrealty.co.zw",
      phone: "+263 77 992 5122",
      whatsapp: "+263 77 992 5122",
      address: "Travel Centre - 1st Floor, Corner Jason Moyo & 3rd St, Harare, Zimbabwe"
    },
    social: {
      twitter: "https://twitter.com/thatonrealty",
      linkedin: "https://linkedin.com/company/thatonrealty",
      facebook: "https://facebook.com/thatonrealty"
    }
  }
};

// Function to detect current brand based on hostname
export function getCurrentBrand(): BrandConfig {
  if (typeof window === 'undefined') {
    return brandConfigs.moyotools; // Default for SSR
  }

  const hostname = window.location.hostname;
  console.log("hostname", hostname);
  // Check for exact domain matches
  for (const [key, config] of Object.entries(brandConfigs)) {
    if (hostname.includes(config.domain.split('.')[0])) {
      return config;
    }
  }
  
  // Check for development/localhost patterns
  console.log("hostname.includes('gerty')", hostname.includes('gerty'));
  if (hostname.includes('thatonrealty')) {
    return brandConfigs.thatonrealty;
  } else if (hostname.includes('gerty') || hostname.includes('localhost')) {
    return brandConfigs.gerty;
  } else if (hostname.includes('moyotools')) {
    return brandConfigs.moyotools;
  } 
  return brandConfigs.gerty;
}

// Function to apply brand colors to CSS variables
export function applyBrandColors(brand: BrandConfig) {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  
  // Convert HSL values to CSS custom properties
  const colors = brand.colors;
  
  root.style.setProperty('--primary', colors.primary.replace('hsl(', '').replace(')', ''));
  root.style.setProperty('--secondary', colors.secondary.replace('hsl(', '').replace(')', ''));
  root.style.setProperty('--accent', colors.accent.replace('hsl(', '').replace(')', ''));
  root.style.setProperty('--muted', colors.muted.replace('hsl(', '').replace(')', ''));
  root.style.setProperty('--background', colors.background.replace('hsl(', '').replace(')', ''));
  root.style.setProperty('--foreground', colors.foreground.replace('hsl(', '').replace(')', ''));
  
  // Update chart colors to match brand
  if (brand.name === 'Gerty') {
    root.style.setProperty('--chart-1', '186 34% 29%'); // turquoise
    root.style.setProperty('--chart-2', '69 35% 69%'); // green
    root.style.setProperty('--chart-3', '210 6% 72%'); // cool grey
    root.style.setProperty('--chart-4', '270 5% 13%'); // charcoal
    root.style.setProperty('--chart-5', '186 34% 39%'); // lighter turquoise
  } else if (brand.name === 'Thaton Realty') {
    root.style.setProperty('--chart-1', '55 11% 58%'); // primary color
    root.style.setProperty('--chart-2', '55 11% 40%'); // darker variant
    root.style.setProperty('--chart-3', '55 11% 75%'); // lighter variant
    root.style.setProperty('--chart-4', '0 0% 60%'); // grey
    root.style.setProperty('--chart-5', '55 11% 30%'); // darkest variant
  } else {
    root.style.setProperty('--chart-1', '221.2 83.2% 53.3%'); // blue
    root.style.setProperty('--chart-2', '262 83% 58%'); // purple
    root.style.setProperty('--chart-3', '199 89% 48%'); // cyan
    root.style.setProperty('--chart-4', '43 96% 58%'); // yellow
    root.style.setProperty('--chart-5', '0 84% 60%'); // red
  }
}

// Function to apply brand-specific favicon
export function applyBrandFavicon(brand: BrandConfig) {
  if (typeof document === 'undefined') return;
  
  // Only update favicon if brand has one configured
  if (!brand.favicon) return;
  
  // Find existing favicon link elements
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  
  // Remove existing favicon links
  existingFavicons.forEach(favicon => favicon.remove());
  
  // Create new favicon link element
  const favicon = document.createElement('link');
  favicon.rel = 'icon';
  favicon.type = 'image/png';
  favicon.href = brand.favicon;
  
  // Also create apple-touch-icon for better mobile support
  const appleFavicon = document.createElement('link');
  appleFavicon.rel = 'apple-touch-icon';
  appleFavicon.href = brand.favicon;
  
  // Add to document head
  document.head.appendChild(favicon);
  document.head.appendChild(appleFavicon);
}

// Hook to use current brand
export function useBrand() {
  return getCurrentBrand();
} 